// utils/window-reload.js

let softRefreshCallback = null;

/**
 * Registers a callback for windowSoftRefresh to call later.
 * Usually used to trigger data reload in React apps.
 * @param {Function} callback - The function to call on soft refresh.
 */
export const registerSoftRefresh = (callback) => {
  softRefreshCallback = callback;
};

/**
 * Unregisters a callback
 */
export const unregisterSoftRefresh = () => {
  softRefreshCallback = null;
};

/**
 * Executes the registered callback to simulate a React "soft refresh".
 * Does not reload the page or touch browser history.
 */
export const windowSoftRefresh = () => {
  if (typeof softRefreshCallback === 'function') {
    softRefreshCallback();
  } else {
    // console.warn('[windowSoftRefresh] No callback registered.');
    windowReloadPage();
  }
};

/**
 * Navigates back to the previous page in the browser's history.
 * Equivalent to pressing the browser's Back button.
 */
export const windowHistoryBack = () => {
  window.history.back();
};

/**
 * Reloads the current page using browser cache.
 * ✅ Simulates: F5 / Reload button
 * ✅ Uses cached resources.
 */
export const windowReloadPage = () => {
  window.location.reload();
};

/**
 * Full reload with cache-busting (appends unique query param).
 * ✅ Simulates: Ctrl + F5 / Cmd + Shift + R
 * ❌ Avoids cache.
 * ❌ Avoids history pollution by using `replace()`.
 */
export const windowForceReload = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('_t', Date.now()); // cache-busting param
  window.location.replace(url.toString());
};

/**
 * Reloads the same or custom URL with optional cache bypassing.
 * ✅ Simulates: 
 *    - F5 if `withCache: true`
 *    - Ctrl + F5 if `withCache: false`
 * ✅ Keeps browser history.
 * 
 * @param {Object} options
 * @param {string} [options.href] - URL to reload (defaults to current)
 * @param {boolean} [options.withCache=false] - Whether to allow cached reload
 */
export const windowForceRefresh = (options = {}) => {
  let { href = window.location.href, withCache = false } = options;

  if (!withCache) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('_t', Date.now());
    href = url.toString();
  }

  window.location.href = href;
};

/**
 * Usage Remains the Same
--- You can call any of these globally:
windowReloadPage();
windowForceReload();
windowForceRefresh({ withCache: false });
windowSoftRefresh();

--- And in React:
useEffect(() => {
  fetchData();
  registerSoftRefresh(fetchData);
  return () => {
    unregisterSoftRefresh(fetchData);
  };
  OR
  const refreshCallback = () => fetchData(item);
  fetchData(item);
  registerSoftRefresh(refreshCallback);
  return () => {
    unregisterSoftRefresh(refreshCallback); // Cleanup
  };
}, []);

OR

const user = useUser(); // Cache local data
const [data, setData] = useState('');
useEffect(() => {
  const reloadData = () => {
    console.log('🔄 Soft refresh triggered');
    setData(`${user?.avatar}?t=${Date.now()}`);
  };

  registerSoftRefresh(reloadData);
  setData(`${user?.avatar}?t=${Date.now()}`);

  return () => { 
    console.log('===== Release ====');
    unregisterSoftRefresh() 
  };
}, [user]);
 */

/**
 * USING when trigger do multiple callbacks
const softRefreshCallbacks = new Set();

// Register a new callback
export const registerSoftRefresh = (callback) => {
  softRefreshCallbacks.add(callback);
};

// Unregister a callback
export const unregisterSoftRefresh = (callback) => {
  softRefreshCallbacks.delete(callback);
};

// Trigger all registered callbacks
export const windowSoftRefresh = () => {
  if (softRefreshCallbacks.size > 0) {
    softRefreshCallbacks.forEach((callback) => {
      if (typeof callback === 'function') callback();
    });
  } else {
    windowReloadPage();
  }
};
*/